/**
 * Single source of truth for whether the current user should see the
 * "RAW" tab on entity-detail modals (DeviceDetailModal, ProfileModal,
 * ...). Mirrors the exact rules already used by DebugMenu so both gates
 * behave consistently:
 *
 *   1. Wait until UiVisibilityContext.ready resolves.
 *   2. If `raw_tab_usernames` is non-empty, only those usernames see it.
 *   3. Otherwise fall back to the global/role `show_raw_tab` boolean.
 *   4. Unset everywhere → default visible (no behaviour change).
 *
 * Returns `null` while the resolution is in flight so callers can keep
 * rendering everything else without painting the RAW tab early.
 */

import { useAuth } from '../contexts/AuthContext';
import { useUiVisibility } from '../contexts/UiVisibilityContext';

export const useRawTabVisible = (): boolean | null => {
  const { user } = useAuth();
  const { showRawTab, rawTabUsernames, ready } = useUiVisibility();

  if (!ready) return null;

  const username = user?.preferredUsername ?? null;
  if (rawTabUsernames.length > 0) {
    return !!username && rawTabUsernames.includes(username);
  }
  return showRawTab;
};
